import { FlexRow, StyledButton } from '@gorila-shared-ui/components';
import { FilterOverrides } from 'baseui/table';
import { TabOverrides, TabsOverrides } from 'baseui/tabs-motion';
import { themedUseStyletron } from '../theme/customTheme';

export function useStyles() {
  const [css, theme] = themedUseStyletron();

  const fillParent = css({
    width: '100%',
    height: '100%',
  });

  const horizontalFlex = css({
    display: 'flex',
  });

  const verticalFlex = css({
    display: 'flex',
    flexDirection: 'column',
  });

  const centeredFlex = css({
    alignItems: 'center',
    justifyContent: 'center',
  });

  const centeredStart = css({
    alignItems: 'center',
    justifyContent: 'start',
  });

  const centeredEnd = css({
    alignItems: 'center',
    justifyContent: 'end',
  });

  const centeredBetween = css({
    alignItems: 'center',
    justifyContent: 'space-between',
  });

  const flexFill = css({
    flex: '1 1 100%',
  });

  const ellipseText = ({ maxLines = 3, breakAll = false }) =>
    css({
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': maxLines,
      display: '-webkit-box',
      overflow: 'hidden',
      wordBreak: breakAll ? 'break-all' : 'break-word',
    });

  const warn = css({
    color: theme.colors.negative,
  });

  const success = css({
    color: theme.colors.positive,
  });

  const colors = {
    warn,
    success,
  };

  const dataHeaderClass = css({
    paddingTop: theme.sizing.scale200,
    paddingBottom: theme.sizing.scale200,
    paddingLeft: theme.sizing.scale600,
    paddingRight: theme.sizing.scale600,
    backgroundColor: theme.colors.backgroundSecondary,
  });

  const highlightDataHeaderClass = css({
    paddingTop: theme.sizing.scale200,
    paddingBottom: theme.sizing.scale200,
    paddingLeft: theme.sizing.scale600,
    paddingRight: theme.sizing.scale600,
    backgroundColor: theme.colors.gray200,
  });

  const dataContentClass = css({
    paddingLeft: theme.sizing.scale600,
    paddingRight: theme.sizing.scale600,
  });

  const customField = css({
    paddingTop: theme.sizing.scale300,
    paddingBottom: theme.sizing.scale300,
    paddingLeft: theme.sizing.scale550,
    paddingRight: theme.sizing.scale550,
    borderRadius: theme.borders.inputBorderRadius,
    backgroundColor: theme.colors.backgroundSecondary,
    width: '100%',
  });

  const flexible = (flex: number) =>
    css({
      flex,
    });

  const basicPageContainer = css({
    padding: theme.sizing.scale400,
    gap: theme.sizing.scale500,
    boxSizing: 'border-box',
  });

  const classes = {
    fillParent,
    horizontalFlex,
    verticalFlex,
    centeredFlex,
    centeredStart,
    centeredEnd,
    flexFill,
    ellipseText,
    flexible,
    dataHeaderClass,
    highlightDataHeaderClass,
    dataContentClass,
    customField,
    centeredBetween,
    basicPageContainer,
  };

  const tabsOverrides: TabsOverrides = {
    Root: {
      style: () => ({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }),
    },
    TabBorder: {
      style: { flexShrink: 0 },
    },
  };

  const tabOverrides: TabOverrides = {
    TabPanel: {
      style: {
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        overflow: 'hidden',
      },
    },
  };

  const tabOverflowOverrides: TabOverrides = {
    TabPanel: {
      style: {
        overflow: 'hidden',
      },
    },
  };

  const filtersOverrides: (onResetFilters: () => void) => FilterOverrides = (onResetFilters: () => void) => ({
    Heading: {
      component: () => <></>,
    },
    Footer: {
      component: () => {
        return (
          <FlexRow
            classNames={css({
              justifyContent: 'end',
              padding: theme.sizing.scale200,
            })}
          >
            <StyledButton
              onClick={onResetFilters}
              size="mini"
              kind="tertiary"
            >
              Limpiar filtros
            </StyledButton>
          </FlexRow>
        );
      },
    },
    Content: {
      style: () => ({
        borderTop: 'none',
        minWidth: '200px',
      }),
    },
  });

  const dividerStyleOverrides = (gap: string = theme.sizing.scale300) => ({ width: '100%', margin: `${gap} 0` });

  const overrides = {
    tabsOverrides,
    tabOverrides,
    tabOverflowOverrides,
    filtersOverrides,
    customDividerStyleOverrides: dividerStyleOverrides,
    dividerStyleOverrides: dividerStyleOverrides(),
  };

  const concatenateClasses = (...classes: string[]) => {
    return classes.join(' ');
  };

  return { css, theme, classes, colors, overrides, concatenateClasses };
}
